'use client'
import AppFooterIcon from 'Comps/svg/AppFooterIcon'
import { useGetHomeRoute } from 'Hooks/useGetHomeRoute'
import { userState } from 'Store/user'
import { APP_HOME_PATHNAME, MobileFooterTag } from 'Types/common'
import { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import useI18n from 'Hooks/useI18n'
import LifecycleLink from '@/components/common/LifecycleLink'
import { homeMobilePageState } from 'Store/home'
import { judgeClient } from '@/utils/bom'
import { withLocale } from '@/locales'
import { AppFooterTabProfileNonLoggedIn } from '@/components-mobile/nav/AppFooterTabProfile'
import dynamic from 'next/dynamic'
import { useLetterReadStatus } from '@/hooks/letter/useLetterReadStatus'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import LazyMount from '@/components/common/LazyMount'
const AppFooterTabProfile = dynamic(
  () => import('@/components-mobile/nav/AppFooterTabProfile'),
  {
    ssr: false,
    loading: () => <AppFooterTabProfileNonLoggedIn />,
  }
)
const MobileLoginDrawer = dynamic(
  () => import('@/components-mobile/modal/MobileLoginDrawer'),
  { ssr: false }
)

const AppFooter = () => {
  const { lang } = useI18n()
  const user = useRecoilValue(userState)
  const { currentBaseRoute } = useGetHomeRoute()
  const setHomePage = useSetRecoilState(homeMobilePageState)
  const [openLoginDrawer, setOpenLoginDrawer] = useState(false)
  const [platform, setPlatform] = useState<'iOS' | 'Android' | 'PC'>()
  const { data: readStatus } = useLetterReadStatus()
  const { trackEvent } = useQuickTracking()
  useEffect(() => {
    function getPlatform() {
      setPlatform(judgeClient())
    }

    if (
      document.readyState === 'complete' ||
      document.readyState === 'interactive'
    ) {
      getPlatform()
    } else {
      window.addEventListener('DOMContentLoaded', getPlatform)
    }

    return () => window.removeEventListener('DOMContentLoaded', getPlatform)
  }, [])

  const handleOpen = () => setOpenLoginDrawer(true)

  return (
    <div
      style={{
        background: `linear-gradient(
            to right bottom,
            rgba(48, 50, 41, 0.75),
            rgba(61, 44, 41, 0.85),
            rgba(67, 46, 60, 0.70)
          )`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 -1px 12px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div className="flex h-14 w-full flex-shrink-0 items-center justify-around md:hidden">
        <LifecycleLink
          href={withLocale(APP_HOME_PATHNAME, lang)}
          prefetch
          onClick={() => {
            setHomePage('screen')
            trackEvent(
              'page_exposure',
              { page_name: 'homepage', platform: 'web_h5' },
              'EXP'
            )
          }}
          className="flex h-full grow items-center justify-center"
        >
          <AppFooterIcon
            type={MobileFooterTag.Home}
            active={currentBaseRoute === MobileFooterTag.Home}
          />
        </LifecycleLink>
        <LifecycleLink
          href={withLocale(APP_HOME_PATHNAME, lang)}
          prefetch
          onClick={() => {
            setHomePage('grid')
            trackEvent(
              'page_exposure',
              { page_name: 'discover', platform: 'web_h5' },
              'EXP'
            )
          }}
          className="flex h-full grow items-center justify-center"
        >
          <AppFooterIcon
            type={MobileFooterTag.Explore}
            active={currentBaseRoute === MobileFooterTag.Explore}
          />
        </LifecycleLink>
        <LifecycleLink
          href={withLocale('/create', lang)}
          prefetch
          className="flex h-full grow items-center justify-center"
          onClick={() => {
            trackEvent(
              'page_exposure',
              { page_name: 'create', platform: 'web_h5' },
              'EXP'
            )
          }}
        >
          <AppFooterIcon
            type={MobileFooterTag.Create}
            active={currentBaseRoute === MobileFooterTag.Create}
          />
        </LifecycleLink>
        <LifecycleLink
          href={withLocale('/chat/recent', lang)}
          onClick={(e) => {
            if (!user?.user_id) {
              e.preventDefault()
              handleOpen()
            }
            trackEvent(
              'page_exposure',
              { page_name: 'chat_list', platform: 'web_h5' },
              'EXP'
            )
          }}
          prefetch
          replace
          className="relative flex h-full grow items-center justify-center"
        >
          <div className="relative h-[24px] w-[24px]">
            <AppFooterIcon
              type={MobileFooterTag.Chat}
              active={currentBaseRoute === MobileFooterTag.Chat}
            />
            {readStatus?.unread_messages && (
              <div className="absolute -right-2 -top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
            )}
          </div>
        </LifecycleLink>
        <AppFooterTabProfile
          active={currentBaseRoute === MobileFooterTag.Profile}
          handleOpen={() => handleOpen()}
          userId={user?.user_id}
          lang={lang}
        />
      </div>
      {/*学佳的 Oppo? 手机自带浏览器刘海屏会把整个页面顶下去，这里从下面撑上来以解决这种情况*/}
      {platform === 'Android' && (
        <div className="pb-[env(safe-area-inset-top,0)]" />
      )}
      <div className="pb-[env(safe-area-inset-bottom,0)]" />

      <LazyMount open={openLoginDrawer}>
        <MobileLoginDrawer
          open={openLoginDrawer}
          setOpen={setOpenLoginDrawer}
        />
      </LazyMount>
    </div>
  )
}

export default AppFooter
